import React, { useState, useEffect, useCallback } from 'react';
import { Card, Row, Col, Form, Button, Table, Spinner, Alert } from 'react-bootstrap';
import axios from 'axios';
import { buildApiUrl } from '../config/api';
import { Category, Subcategory, FullEquipment, Consumable } from '../interfaces';
import { useAuth } from '../contexts/AuthContext';

interface LongEventRecommendation {
  id: number;
  equipment: number;
  quantity: number;
  equipment_name?: string;
  equipment_category_name?: string;
  equipment_subcategory_name?: string;
}

interface InternalExternalRecommendation {
  id: number;
  equipment: number;
  is_internal: boolean;
  quantity: number;
  equipment_name?: string;
  equipment_category_name?: string;
  equipment_subcategory_name?: string;
}

interface LongEventConsumableRecommendation {
  id: number;
  consumable: number;
  quantity: number;
  consumable_name?: string;
}

interface InternalExternalConsumableRecommendation {
  id: number;
  consumable: number;
  is_internal: boolean;
  quantity: number;
  consumable_name?: string;
}

const LONG_EVENT_API = 'complementary-equipment/long-event/';
const INTERNAL_EXTERNAL_API = 'complementary-equipment/internal-external-event/';
const LONG_EVENT_CONS_API = 'complementary-equipment/long-event-consumable/';
const INTERNAL_EXTERNAL_CONS_API = 'complementary-equipment/internal-external-event-consumable/';

type PickerState = {
  category: number | '';
  subcategory: number | '';
  equipmentId: number | '';
  quantity: number;
};

const initialPicker: PickerState = {
  category: '',
  subcategory: '',
  equipmentId: '',
  quantity: 1,
};

type ConsumablePickerState = { consumableId: number | ''; quantity: number };
const initialConsumablePicker: ConsumablePickerState = { consumableId: '', quantity: 1 };

interface ConsumablePickerBlockProps {
  title: string;
  pickerId: string;
  disabled: boolean;
  allConsumables: Consumable[];
  picker: ConsumablePickerState;
  setPicker: React.Dispatch<React.SetStateAction<ConsumablePickerState>>;
  onAdd: () => void;
}

const ConsumablePickerBlock: React.FC<ConsumablePickerBlockProps> = ({
  title,
  pickerId,
  disabled,
  allConsumables,
  picker,
  setPicker,
  onAdd,
}) => (
  <>
    {title ? <h6 className="mb-3 text-muted">{title}</h6> : null}
    <Row className="mb-3">
      <Col md={8}>
        <Form.Group controlId={`rec-cons-${pickerId}`}>
          <Form.Label>מתכלה</Form.Label>
          <Form.Select
            value={picker.consumableId === '' ? '' : String(picker.consumableId)}
            onChange={(e) => {
              const v = e.target.value;
              setPicker({
                ...initialConsumablePicker,
                consumableId: v === '' ? '' : parseInt(v, 10),
              });
            }}
            disabled={disabled}
          >
            <option value="">בחר מתכלה...</option>
            {[...allConsumables]
              .sort((a, b) => (a.name || '').localeCompare(b.name || '', 'he'))
              .map((c) => (
                <option key={c.id} value={c.id}>
                  {c.name}
                </option>
              ))}
          </Form.Select>
        </Form.Group>
      </Col>
      <Col md={4}>
        <Form.Group controlId={`rec-cons-qty-${pickerId}`}>
          <Form.Label>כמות</Form.Label>
          <Form.Control
            type="number"
            min={1}
            value={picker.quantity}
            onChange={(e) =>
              setPicker((prev) => ({ ...prev, quantity: parseInt(e.target.value, 10) || 1 }))
            }
            disabled={disabled || !picker.consumableId}
          />
        </Form.Group>
      </Col>
    </Row>
    <Row className="mb-3">
      <Col>
        <Button
          variant="outline-secondary"
          className="w-100"
          disabled={disabled || !picker.consumableId || picker.quantity <= 0}
          onClick={onAdd}
        >
          הוסף מתכלה לרשימה
        </Button>
      </Col>
    </Row>
  </>
);

interface EquipmentPickerBlockProps {
  title: string;
  /** מזהה ייחודי לשדות טופס (אנגלית בלבד) */
  pickerId: string;
  disabled: boolean;
  categories: Category[];
  subcategories: Subcategory[];
  allEquipment: FullEquipment[];
  picker: PickerState;
  setPicker: React.Dispatch<React.SetStateAction<PickerState>>;
  onAdd: () => void;
}

const EquipmentPickerBlock: React.FC<EquipmentPickerBlockProps> = ({
  title,
  pickerId,
  disabled,
  categories,
  subcategories,
  allEquipment,
  picker,
  setPicker,
  onAdd,
}) => {
  const filteredSubcategories = picker.category
    ? subcategories.filter((s) => s.category === Number(picker.category))
    : [];

  const availableEquipment = picker.subcategory
    ? allEquipment.filter((eq) => eq.subcategory === Number(picker.subcategory))
    : [];

  return (
    <>
      <h6 className="mb-3 text-muted">{title}</h6>
      <Row className="mb-3">
        <Col md={4}>
          <Form.Group controlId={`rec-cat-${pickerId}`}>
            <Form.Label>קטגוריה</Form.Label>
            <Form.Select
              value={picker.category === '' ? '' : String(picker.category)}
              onChange={(e) => {
                const v = e.target.value;
                setPicker({
                  ...initialPicker,
                  category: v === '' ? '' : parseInt(v, 10),
                });
              }}
              disabled={disabled}
            >
              <option value="">בחר קטגוריה...</option>
              {categories.map((cat) => (
                <option key={cat.id} value={cat.id}>
                  {cat.category_name}
                </option>
              ))}
            </Form.Select>
          </Form.Group>
        </Col>
        <Col md={4}>
          <Form.Group controlId={`rec-sub-${pickerId}`}>
            <Form.Label>תת-קטגוריה</Form.Label>
            <Form.Select
              value={picker.subcategory === '' ? '' : String(picker.subcategory)}
              onChange={(e) => {
                const v = e.target.value;
                setPicker((prev) => ({
                  ...prev,
                  subcategory: v === '' ? '' : parseInt(v, 10),
                  equipmentId: '',
                }));
              }}
              disabled={disabled || !picker.category}
            >
              <option value="">בחר תת-קטגוריה...</option>
              {filteredSubcategories.map((sub) => (
                <option key={sub.id} value={sub.id}>
                  {sub.subcategory_name}
                </option>
              ))}
            </Form.Select>
          </Form.Group>
        </Col>
        <Col md={4}>
          <Form.Group controlId={`rec-eq-${pickerId}`}>
            <Form.Label>פריט ציוד</Form.Label>
            <Form.Select
              value={picker.equipmentId === '' ? '' : String(picker.equipmentId)}
              onChange={(e) => {
                const v = e.target.value;
                setPicker((prev) => ({
                  ...prev,
                  equipmentId: v === '' ? '' : parseInt(v, 10),
                }));
              }}
              disabled={disabled || !picker.subcategory}
            >
              <option value="">בחר פריט...</option>
              {availableEquipment.map((item) => (
                <option key={item.id} value={item.id}>
                  {item.name}
                </option>
              ))}
            </Form.Select>
          </Form.Group>
        </Col>
      </Row>
      <Row className="mb-3">
        <Col md={6}>
          <Form.Group controlId={`rec-qty-${pickerId}`}>
            <Form.Label>כמות מומלצת</Form.Label>
            <Form.Control
              type="number"
              min={1}
              value={picker.quantity}
              onChange={(e) =>
                setPicker((prev) => ({ ...prev, quantity: parseInt(e.target.value, 10) || 1 }))
              }
              disabled={disabled || !picker.equipmentId}
            />
          </Form.Group>
        </Col>
        <Col md={6} className="d-flex align-items-end">
          <Button
            variant="outline-primary"
            className="w-100"
            disabled={disabled || !picker.equipmentId || picker.quantity <= 0}
            onClick={onAdd}
          >
            הוסף לרשימה
          </Button>
        </Col>
      </Row>
    </>
  );
};

const EquipmentRecommendationsTab: React.FC = () => {
  const { canEditEquipment, isWarehouse } = useAuth();
  const canModify = !!canEditEquipment;
  const deleteDisabled = !!isWarehouse;

  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [categories, setCategories] = useState<Category[]>([]);
  const [subcategories, setSubcategories] = useState<Subcategory[]>([]);
  const [allEquipment, setAllEquipment] = useState<FullEquipment[]>([]);
  const [longEventItems, setLongEventItems] = useState<LongEventRecommendation[]>([]);
  const [internalExternalItems, setInternalExternalItems] = useState<InternalExternalRecommendation[]>([]);
  const [longEventConsItems, setLongEventConsItems] = useState<LongEventConsumableRecommendation[]>([]);
  const [internalExternalConsItems, setInternalExternalConsItems] = useState<InternalExternalConsumableRecommendation[]>(
    []
  );
  const [allConsumables, setAllConsumables] = useState<Consumable[]>([]);

  const [pickerLong, setPickerLong] = useState<PickerState>(initialPicker);
  const [pickerInternal, setPickerInternal] = useState<PickerState>(initialPicker);
  const [pickerExternal, setPickerExternal] = useState<PickerState>(initialPicker);
  const [pickerLongCons, setPickerLongCons] = useState<ConsumablePickerState>(initialConsumablePicker);
  const [pickerInternalCons, setPickerInternalCons] = useState<ConsumablePickerState>(initialConsumablePicker);
  const [pickerExternalCons, setPickerExternalCons] = useState<ConsumablePickerState>(initialConsumablePicker);

  const loadRecommendations = useCallback(async () => {
    const [longRes, ieRes, longConsRes, ieConsRes] = await Promise.all([
      axios.get<LongEventRecommendation[]>(buildApiUrl(LONG_EVENT_API)),
      axios.get<InternalExternalRecommendation[]>(buildApiUrl(INTERNAL_EXTERNAL_API)),
      axios.get<LongEventConsumableRecommendation[]>(buildApiUrl(LONG_EVENT_CONS_API)),
      axios.get<InternalExternalConsumableRecommendation[]>(buildApiUrl(INTERNAL_EXTERNAL_CONS_API)),
    ]);
    setLongEventItems(longRes.data);
    setInternalExternalItems(ieRes.data);
    setLongEventConsItems(longConsRes.data);
    setInternalExternalConsItems(ieConsRes.data);
  }, []);

  useEffect(() => {
    const load = async () => {
      setLoading(true);
      setError(null);
      try {
        const [catRes, subRes, eqRes, consRes] = await Promise.all([
          axios.get<Category[]>(buildApiUrl('settings/categories/')),
          axios.get<Subcategory[]>(buildApiUrl('settings/subcategories/')),
          axios.get<FullEquipment[]>(buildApiUrl('inventory/equipment/')),
          axios.get<Consumable[]>(buildApiUrl('settings/consumables/')),
        ]);
        setCategories(catRes.data);
        setSubcategories(subRes.data);
        setAllEquipment(eqRes.data);
        setAllConsumables(consRes.data);
        await loadRecommendations();
      } catch {
        setError('לא ניתן לטעון נתוני המלצות. וודא שהשרת זמין.');
      } finally {
        setLoading(false);
      }
    };
    load();
  }, [loadRecommendations]);

  const parseApiError = (err: unknown): string => {
    if (axios.isAxiosError(err) && err.response?.data) {
      const d = err.response.data;
      if (typeof d === 'object' && d !== null) {
        const parts: string[] = [];
        for (const key of Object.keys(d)) {
          const val = (d as Record<string, unknown>)[key];
          if (Array.isArray(val)) parts.push(`${key}: ${val.join(', ')}`);
          else if (val != null) parts.push(`${key}: ${String(val)}`);
        }
        return parts.join('\n') || 'שגיאה לא ידועה';
      }
    }
    return 'שגיאה בשמירה. נסה שוב.';
  };

  const handleAddLong = async () => {
    if (!pickerLong.equipmentId || pickerLong.quantity <= 0) return;
    setError(null);
    const eqId = Number(pickerLong.equipmentId);
    const existing = longEventItems.find((r) => r.equipment === eqId);
    try {
      if (existing) {
        await axios.patch(buildApiUrl(`${LONG_EVENT_API}${existing.id}/`), {
          quantity: existing.quantity + pickerLong.quantity,
        });
      } else {
        await axios.post(buildApiUrl(LONG_EVENT_API), {
          equipment: eqId,
          quantity: pickerLong.quantity,
        });
      }
      setPickerLong(initialPicker);
      await loadRecommendations();
    } catch (err) {
      setError(parseApiError(err));
    }
  };

  const handleRemoveLong = async (id: number) => {
    setError(null);
    try {
      await axios.delete(buildApiUrl(`${LONG_EVENT_API}${id}/`));
      await loadRecommendations();
    } catch (err) {
      setError(parseApiError(err));
    }
  };

  const handleAddInternalExternal = async (isInternal: boolean, picker: PickerState, reset: () => void) => {
    if (!picker.equipmentId || picker.quantity <= 0) return;
    setError(null);
    const eqId = Number(picker.equipmentId);
    const existing = internalExternalItems.find(
      (r) => r.equipment === eqId && r.is_internal === isInternal
    );
    try {
      if (existing) {
        await axios.patch(buildApiUrl(`${INTERNAL_EXTERNAL_API}${existing.id}/`), {
          quantity: existing.quantity + picker.quantity,
        });
      } else {
        await axios.post(buildApiUrl(INTERNAL_EXTERNAL_API), {
          equipment: eqId,
          is_internal: isInternal,
          quantity: picker.quantity,
        });
      }
      reset();
      await loadRecommendations();
    } catch (err) {
      setError(parseApiError(err));
    }
  };

  const handleRemoveInternalExternal = async (id: number) => {
    setError(null);
    try {
      await axios.delete(buildApiUrl(`${INTERNAL_EXTERNAL_API}${id}/`));
      await loadRecommendations();
    } catch (err) {
      setError(parseApiError(err));
    }
  };

  const handleAddLongCons = async () => {
    if (!pickerLongCons.consumableId || pickerLongCons.quantity <= 0) return;
    setError(null);
    const cid = Number(pickerLongCons.consumableId);
    const existing = longEventConsItems.find((r) => r.consumable === cid);
    try {
      if (existing) {
        await axios.patch(buildApiUrl(`${LONG_EVENT_CONS_API}${existing.id}/`), {
          quantity: existing.quantity + pickerLongCons.quantity,
        });
      } else {
        await axios.post(buildApiUrl(LONG_EVENT_CONS_API), {
          consumable: cid,
          quantity: pickerLongCons.quantity,
        });
      }
      setPickerLongCons(initialConsumablePicker);
      await loadRecommendations();
    } catch (err) {
      setError(parseApiError(err));
    }
  };

  const handleRemoveLongCons = async (id: number) => {
    setError(null);
    try {
      await axios.delete(buildApiUrl(`${LONG_EVENT_CONS_API}${id}/`));
      await loadRecommendations();
    } catch (err) {
      setError(parseApiError(err));
    }
  };

  const handleAddInternalExternalCons = async (
    isInternal: boolean,
    picker: ConsumablePickerState,
    reset: () => void
  ) => {
    if (!picker.consumableId || picker.quantity <= 0) return;
    setError(null);
    const cid = Number(picker.consumableId);
    const existing = internalExternalConsItems.find(
      (r) => r.consumable === cid && r.is_internal === isInternal
    );
    try {
      if (existing) {
        await axios.patch(buildApiUrl(`${INTERNAL_EXTERNAL_CONS_API}${existing.id}/`), {
          quantity: existing.quantity + picker.quantity,
        });
      } else {
        await axios.post(buildApiUrl(INTERNAL_EXTERNAL_CONS_API), {
          consumable: cid,
          is_internal: isInternal,
          quantity: picker.quantity,
        });
      }
      reset();
      await loadRecommendations();
    } catch (err) {
      setError(parseApiError(err));
    }
  };

  const handleRemoveInternalExternalCons = async (id: number) => {
    setError(null);
    try {
      await axios.delete(buildApiUrl(`${INTERNAL_EXTERNAL_CONS_API}${id}/`));
      await loadRecommendations();
    } catch (err) {
      setError(parseApiError(err));
    }
  };

  const internalItems = internalExternalItems.filter((r) => r.is_internal);
  const externalItems = internalExternalItems.filter((r) => !r.is_internal);
  const internalConsItems = internalExternalConsItems.filter((r) => r.is_internal);
  const externalConsItems = internalExternalConsItems.filter((r) => !r.is_internal);

  if (loading) {
    return (
      <div className="text-center py-5">
        <Spinner animation="border" />
        <p className="mt-2 text-muted">טוען המלצות...</p>
      </div>
    );
  }

  return (
    <div className="p-2">
      <h4 className="mb-2">ניהול המלצות ציוד</h4>
      <p className="text-muted mb-4">
        כאן מגדירים <strong>ציוד ומתכלים</strong> שמוצעים אוטומטית בהכנת אירוע: לפי <strong>אורך האירוע</strong> (אירוע
        ארוך) ולפי <strong>אירוע פנימי או חיצוני</strong>. בחירת ציוד דומה ל&quot;ציוד משלים&quot; בטופס ציוד; מתכלים נבחרים
        מרשימת המתכלים במערכת.
      </p>

      {error && (
        <Alert variant="danger" dismissible onClose={() => setError(null)}>
          {error}
        </Alert>
      )}

      {!canModify && (
        <Alert variant="info" className="mb-4">
          יש לך הרשאת צפייה בלבד. לשינוי הרשימות נדרשת הרשאת עריכת ציוד.
        </Alert>
      )}

      <Row className="g-4">
        <Col lg={12}>
          <Card className="shadow-sm">
            <Card.Header className="fw-semibold">המלצות לאירוע ארוך</Card.Header>
            <Card.Body>
              <p className="small text-muted mb-3">
                פריטים ברשימה זו מיועדים להמלצה כאשר האירוע נחשב ארוך (למשל מעל יום אחד) — בהתאם ללוגיקה
                במערכת האירועים.
              </p>
              <EquipmentPickerBlock
                title="הוספת פריט מומלץ"
                pickerId="long-event"
                disabled={!canModify}
                categories={categories}
                subcategories={subcategories}
                allEquipment={allEquipment}
                picker={pickerLong}
                setPicker={setPickerLong}
                onAdd={handleAddLong}
              />
              {longEventItems.length > 0 && (
                <div className="mt-4">
                  <h6>פריטים מומלצים לאירוע ארוך</h6>
                  <Table striped bordered hover size="sm" responsive>
                    <thead>
                      <tr>
                        <th>שם פריט</th>
                        <th>קטגוריה</th>
                        <th>תת-קטגוריה</th>
                        <th>כמות</th>
                        <th>פעולות</th>
                      </tr>
                    </thead>
                    <tbody>
                      {longEventItems.map((row) => (
                        <tr key={row.id}>
                          <td>{row.equipment_name}</td>
                          <td>{row.equipment_category_name ?? '—'}</td>
                          <td>{row.equipment_subcategory_name ?? '—'}</td>
                          <td>{row.quantity}</td>
                          <td>
                            <Button
                              variant="danger"
                              size="sm"
                              disabled={!canModify || deleteDisabled}
                              title={deleteDisabled ? 'למחסנאי אין הרשאה למחיקה' : undefined}
                              onClick={() => handleRemoveLong(row.id)}
                            >
                              הסר
                            </Button>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </Table>
                </div>
              )}

              <hr className="my-4" />
              <h6 className="fw-semibold mb-2">מתכלים מומלצים לאירוע ארוך</h6>
              <ConsumablePickerBlock
                title="הוספת מתכלה"
                pickerId="long-event-cons"
                disabled={!canModify}
                allConsumables={allConsumables}
                picker={pickerLongCons}
                setPicker={setPickerLongCons}
                onAdd={handleAddLongCons}
              />
              {longEventConsItems.length > 0 && (
                <Table striped bordered hover size="sm" responsive className="mt-2">
                  <thead>
                    <tr>
                      <th>שם</th>
                      <th>כמות</th>
                      <th>פעולות</th>
                    </tr>
                  </thead>
                  <tbody>
                    {longEventConsItems.map((row) => (
                      <tr key={row.id}>
                        <td>{row.consumable_name}</td>
                        <td>{row.quantity}</td>
                        <td>
                          <Button
                            variant="danger"
                            size="sm"
                            disabled={!canModify || deleteDisabled}
                            title={deleteDisabled ? 'למחסנאי אין הרשאה למחיקה' : undefined}
                            onClick={() => handleRemoveLongCons(row.id)}
                          >
                            הסר
                          </Button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </Table>
              )}
            </Card.Body>
          </Card>
        </Col>

        <Col lg={6}>
          <Card className="shadow-sm h-100">
            <Card.Header className="fw-semibold">אירוע בתוך מבנה (פנימי)</Card.Header>
            <Card.Body>
              <EquipmentPickerBlock
                title="הוספת פריט מומלץ (פנימי)"
                pickerId="event-internal"
                disabled={!canModify}
                categories={categories}
                subcategories={subcategories}
                allEquipment={allEquipment}
                picker={pickerInternal}
                setPicker={setPickerInternal}
                onAdd={() =>
                  handleAddInternalExternal(true, pickerInternal, () => setPickerInternal(initialPicker))
                }
              />
              {internalItems.length > 0 && (
                <div className="mt-4">
                  <h6>רשימה</h6>
                  <Table striped bordered hover size="sm" responsive>
                    <thead>
                      <tr>
                        <th>שם פריט</th>
                        <th>קטגוריה</th>
                        <th>תת-קטגוריה</th>
                        <th>כמות</th>
                        <th>פעולות</th>
                      </tr>
                    </thead>
                    <tbody>
                      {internalItems.map((row) => (
                        <tr key={row.id}>
                          <td>{row.equipment_name}</td>
                          <td>{row.equipment_category_name ?? '—'}</td>
                          <td>{row.equipment_subcategory_name ?? '—'}</td>
                          <td>{row.quantity}</td>
                          <td>
                            <Button
                              variant="danger"
                              size="sm"
                              disabled={!canModify || deleteDisabled}
                              title={deleteDisabled ? 'למחסנאי אין הרשאה למחיקה' : undefined}
                              onClick={() => handleRemoveInternalExternal(row.id)}
                            >
                              הסר
                            </Button>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </Table>
                </div>
              )}

              <hr className="my-3" />
              <h6 className="small fw-semibold text-muted mb-2">מתכלים — אירוע בתוך מבנה (פנימי)</h6>
              <ConsumablePickerBlock
                title=""
                pickerId="event-internal-cons"
                disabled={!canModify}
                allConsumables={allConsumables}
                picker={pickerInternalCons}
                setPicker={setPickerInternalCons}
                onAdd={() =>
                  handleAddInternalExternalCons(true, pickerInternalCons, () =>
                    setPickerInternalCons(initialConsumablePicker)
                  )
                }
              />
              {internalConsItems.length > 0 && (
                <Table striped bordered hover size="sm" responsive className="mt-2">
                  <thead>
                    <tr>
                      <th>שם</th>
                      <th>כמות</th>
                      <th>פעולות</th>
                    </tr>
                  </thead>
                  <tbody>
                    {internalConsItems.map((row) => (
                      <tr key={row.id}>
                        <td>{row.consumable_name}</td>
                        <td>{row.quantity}</td>
                        <td>
                          <Button
                            variant="danger"
                            size="sm"
                            disabled={!canModify || deleteDisabled}
                            onClick={() => handleRemoveInternalExternalCons(row.id)}
                          >
                            הסר
                          </Button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </Table>
              )}
            </Card.Body>
          </Card>
        </Col>

        <Col lg={6}>
          <Card className="shadow-sm h-100">
            <Card.Header className="fw-semibold">אירוע חיצוני (לא בתוך מבנה)</Card.Header>
            <Card.Body>
              <EquipmentPickerBlock
                title="הוספת פריט מומלץ (חיצוני)"
                pickerId="event-external"
                disabled={!canModify}
                categories={categories}
                subcategories={subcategories}
                allEquipment={allEquipment}
                picker={pickerExternal}
                setPicker={setPickerExternal}
                onAdd={() =>
                  handleAddInternalExternal(false, pickerExternal, () => setPickerExternal(initialPicker))
                }
              />
              {externalItems.length > 0 && (
                <div className="mt-4">
                  <h6>רשימה</h6>
                  <Table striped bordered hover size="sm" responsive>
                    <thead>
                      <tr>
                        <th>שם פריט</th>
                        <th>קטגוריה</th>
                        <th>תת-קטגוריה</th>
                        <th>כמות</th>
                        <th>פעולות</th>
                      </tr>
                    </thead>
                    <tbody>
                      {externalItems.map((row) => (
                        <tr key={row.id}>
                          <td>{row.equipment_name}</td>
                          <td>{row.equipment_category_name ?? '—'}</td>
                          <td>{row.equipment_subcategory_name ?? '—'}</td>
                          <td>{row.quantity}</td>
                          <td>
                            <Button
                              variant="danger"
                              size="sm"
                              disabled={!canModify || deleteDisabled}
                              title={deleteDisabled ? 'למחסנאי אין הרשאה למחיקה' : undefined}
                              onClick={() => handleRemoveInternalExternal(row.id)}
                            >
                              הסר
                            </Button>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </Table>
                </div>
              )}

              <hr className="my-3" />
              <h6 className="small fw-semibold text-muted mb-2">מתכלים — אירוע חיצוני (לא בתוך מבנה)</h6>
              <ConsumablePickerBlock
                title=""
                pickerId="event-external-cons"
                disabled={!canModify}
                allConsumables={allConsumables}
                picker={pickerExternalCons}
                setPicker={setPickerExternalCons}
                onAdd={() =>
                  handleAddInternalExternalCons(false, pickerExternalCons, () =>
                    setPickerExternalCons(initialConsumablePicker)
                  )
                }
              />
              {externalConsItems.length > 0 && (
                <Table striped bordered hover size="sm" responsive className="mt-2">
                  <thead>
                    <tr>
                      <th>שם</th>
                      <th>כמות</th>
                      <th>פעולות</th>
                    </tr>
                  </thead>
                  <tbody>
                    {externalConsItems.map((row) => (
                      <tr key={row.id}>
                        <td>{row.consumable_name}</td>
                        <td>{row.quantity}</td>
                        <td>
                          <Button
                            variant="danger"
                            size="sm"
                            disabled={!canModify || deleteDisabled}
                            onClick={() => handleRemoveInternalExternalCons(row.id)}
                          >
                            הסר
                          </Button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </Table>
              )}
            </Card.Body>
          </Card>
        </Col>
      </Row>
    </div>
  );
};

export default EquipmentRecommendationsTab;
